/**
 * AddressForm — Todo-en-uno
 *
 * Flujo MX:  CP → colonias dropdown → municipio/estado auto → calle
 *            → botón "Verificar y calcular" → SkyDropX cotiza + tarifas inline
 *
 * Flujo US:  ZIP → city/state auto → calle
 *            → botón "Verify & calculate" → SkyDropX cotiza + tarifas inline
 *
 * Props:
 *   region        'mx' | 'us'
 *   lang          'es' | 'en'
 *   totalWeightKg number
 *   lengthCm / widthCm / heightCm  number
 *   onChange      (address) => void
 *   onRateSelect  (rate, quotationId) => void
 */
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { FlagImg } from './FlagImg';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const fadeIn = keyframes`from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:translateY(0);}`;
const spin   = keyframes`to{transform:rotate(360deg);}`;

/* ── Estilos ── */
const Wrap = styled.div`
  margin-top:28px; padding:24px; background:#FFFDF7;
  border:1px solid rgba(26,18,8,.1); border-radius:16px;
  @media(max-width:500px){ padding:16px; }
`;
const SecTitle = styled.p`
  font-family:'Raleway',sans-serif; font-size:.65rem; letter-spacing:.15em;
  color:#C07020; margin:0 0 18px;
`;
const Grid = styled.div`
  display:grid; grid-template-columns:1fr 1fr; gap:12px;
  @media(max-width:600px){ grid-template-columns:1fr; }
`;
const Full = styled.div`grid-column:1/-1;`;
const Label = styled.label`
  display:block; font-size:.72rem; font-weight:600;
  color:rgba(26,18,8,.55); margin-bottom:4px; letter-spacing:.05em;
`;
const Input = styled.input`
  width:100%; padding:10px 13px;
  border:1px solid ${({ $e }) => $e ? '#E05252' : 'rgba(26,18,8,.14)'};
  border-radius:9px; font-size:.88rem; color:#1A1208; background:#fff;
  outline:none; box-sizing:border-box; transition:border-color .15s;
  &:focus{ border-color:#C07020; }
  &:read-only{ background:#F5EFE6; color:#7C3A00; font-weight:700; cursor:default; }
`;
const Sel = styled.select`
  width:100%; padding:10px 32px 10px 13px;
  border:1px solid ${({ $e }) => $e ? '#E05252' : 'rgba(26,18,8,.14)'};
  border-radius:9px; font-size:.88rem;
  color:${({ $ph }) => $ph ? 'rgba(26,18,8,.38)' : '#1A1208'};
  background:#fff; outline:none; cursor:pointer; appearance:none;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%231A1208' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat:no-repeat; background-position:right 10px center; background-color:#fff;
  box-sizing:border-box;
  &:disabled{ background:rgba(26,18,8,.04); color:rgba(26,18,8,.35); cursor:not-allowed; }
`;
const DialSel = styled.select`
  padding:10px 26px 10px 10px; flex-shrink:0; width:118px;
  border:1px solid rgba(26,18,8,.14); border-radius:9px; font-size:.82rem;
  color:#1A1208; background:#fff; outline:none; cursor:pointer; appearance:none;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='7' viewBox='0 0 10 7'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%231A1208' stroke-width='1.5' fill='none'/%3E%3C/svg%3E");
  background-repeat:no-repeat; background-position:right 7px center;
  &:focus{ border-color:#C07020; }
  @media(max-width:400px){ width:96px; font-size:.75rem; padding-left:7px; }
`;
const PhoneRow  = styled.div`
  display:flex; gap:8px;
  @media(max-width:400px){ gap:6px; }
`;
const PhoneInp  = styled(Input)`flex:1; width:auto;`;
const Err  = styled.p`color:#E05252; font-size:.72rem; margin:3px 0 0;`;
const Hint = styled.p`color:rgba(26,18,8,.4); font-size:.7rem; margin:3px 0 0;`;
const CountryNote = styled.p`
  font-size:.7rem; color:rgba(26,18,8,.4); margin:0 0 10px; line-height:1.5;
`;
const RegionToggle = styled.div`
  display:flex; align-items:center; width:100%;
  background:#FAF3E0; border:1px solid #E8DFD0;
  border-radius:999px; padding:3px;
`;
const RegionBtn = styled.button`
  flex:1; display:flex; align-items:center; justify-content:center; gap:7px;
  padding:8px 10px; border-radius:999px;
  font-size:.78rem; font-weight:600; letter-spacing:.03em; white-space:nowrap;
  color:${({ $active }) => $active ? '#FFFDF7' : 'rgba(26,18,8,.55)'};
  background:${({ $active }) => $active ? '#7C3A00' : 'transparent'};
  transition:background .2s, color .2s;
  &:hover:not([data-active="true"]){ color:#7C3A00; }
  @media(max-width:480px){ gap:0; padding:8px 6px; }
`;
const BtnLabel = styled.span`
  @media(max-width:480px){ display:none; }
`;
const Spin = styled.span`
  display:inline-block; width:14px; height:14px;
  border:2px solid rgba(192,112,32,.25); border-top-color:#C07020;
  border-radius:50%; animation:${spin} .7s linear infinite; vertical-align:middle;
`;
const CalcBtn = styled.button`
  width:100%; padding:14px; border:none; border-radius:12px;
  background:${({ disabled }) => disabled ? 'rgba(26,18,8,.07)' : '#7C3A00'};
  color:${({ disabled }) => disabled ? 'rgba(26,18,8,.28)' : '#FFFDF7'};
  font-size:.88rem; font-weight:700; letter-spacing:.06em;
  cursor:${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  display:flex; align-items:center; justify-content:center; gap:8px;
  margin-top:4px; transition:background .2s, transform .15s;
  &:hover:not(:disabled){ background:#C07020; transform:translateY(-1px); }
`;
const ResultBox = styled.div`
  margin-top:16px; padding:16px; border-radius:12px;
  background:${({ $ok }) => $ok ? '#F0FBF4' : $ok === false ? '#FFF5F5' : '#FFFBF0'};
  border:1px solid ${({ $ok }) => $ok ? '#B7DFCA' : $ok === false ? '#F5C2C2' : '#F0D888'};
  animation:${fadeIn} .3s ease;
`;
const Badge    = styled.div`font-size:.84rem; font-weight:700; color:${({ $ok }) => $ok ? '#1A6E3E' : $ok === false ? '#C0392B' : '#7C5B00'}; margin-bottom:8px;`;
const NormAddr = styled.p`font-size:.76rem; color:rgba(26,18,8,.5); margin:0 0 14px; line-height:1.5;`;
const RateList = styled.div`display:flex; flex-direction:column; gap:8px;`;
const RateOpt  = styled.label`
  display:flex; align-items:center; gap:12px; padding:12px 14px;
  border:2px solid ${({ $sel }) => $sel ? '#7C3A00' : 'rgba(26,18,8,.1)'};
  border-radius:10px; background:${({ $sel }) => $sel ? 'rgba(124,58,0,.04)' : '#fff'};
  cursor:pointer; transition:border-color .15s;
  &:hover{ border-color:#C07020; }
`;
const RRadio = styled.input`accent-color:#7C3A00; width:16px; height:16px; flex-shrink:0;`;
const RInfo  = styled.div`flex:1;`;
const RName  = styled.div`font-weight:700; font-size:.86rem; color:#1A1208;`;
const RDays  = styled.div`font-size:.74rem; color:rgba(26,18,8,.45); margin-top:2px;`;
const RPrice = styled.div`font-weight:700; font-size:.95rem; color:#7C3A00; white-space:nowrap;`;
const Divider = styled.div`border-top:1px dashed rgba(26,18,8,.1); margin:20px 0 14px;`;

/* ── Helpers ── */
const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],
  ['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],
  ['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],
  ['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],
  ['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],
  ['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],
  ['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],
  ['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],
  ['WI','Wisconsin'],['WY','Wyoming'],['DC','Washington D.C.'],
  ['PR','Puerto Rico'],['GU','Guam'],['VI','U.S. Virgin Islands'],
];

const MX_STATES = [
  ['AGS','Aguascalientes'],['BC','Baja California'],['BCS','Baja California Sur'],
  ['CAMP','Campeche'],['CHIS','Chiapas'],['CHIH','Chihuahua'],['CDMX','Ciudad de México'],
  ['COAH','Coahuila'],['COL','Colima'],['DGO','Durango'],['GTO','Guanajuato'],
  ['GRO','Guerrero'],['HGO','Hidalgo'],['JAL','Jalisco'],['MEX','México'],
  ['MICH','Michoacán'],['MOR','Morelos'],['NAY','Nayarit'],['NLE','Nuevo León'],
  ['OAX','Oaxaca'],['PUE','Puebla'],['QRO','Querétaro'],['QROO','Quintana Roo'],
  ['SLP','San Luis Potosí'],['SIN','Sinaloa'],['SON','Sonora'],['TAB','Tabasco'],
  ['TAMS','Tamaulipas'],['TLAX','Tlaxcala'],['VER','Veracruz'],['YUC','Yucatán'],
  ['ZAC','Zacatecas'],
];

const CA_PROVINCES = [
  ['AB','Alberta'],['BC','British Columbia'],['MB','Manitoba'],
  ['NB','New Brunswick'],['NL','Newfoundland and Labrador'],['NS','Nova Scotia'],
  ['NT','Northwest Territories'],['NU','Nunavut'],['ON','Ontario'],
  ['PE','Prince Edward Island'],['QC','Quebec'],['SK','Saskatchewan'],
  ['YT','Yukon'],
];

const DEST_COUNTRIES = [
  { code:'US', label:'United States' },
  { code:'CA', label:'Canada' },
];

const DIALS = [
  { code:'MX', dial:'+52',  flag:'🇲🇽', label:'México'    },
  { code:'US', dial:'+1',   flag:'🇺🇸', label:'EE.UU.'    },
  { code:'CA', dial:'+1',   flag:'🇨🇦', label:'Canadá'    },
  { code:'ES', dial:'+34',  flag:'🇪🇸', label:'España'    },
  { code:'AR', dial:'+54',  flag:'🇦🇷', label:'Argentina' },
  { code:'CO', dial:'+57',  flag:'🇨🇴', label:'Colombia'  },
  { code:'CL', dial:'+56',  flag:'🇨🇱', label:'Chile'     },
  { code:'PE', dial:'+51',  flag:'🇵🇪', label:'Perú'      },
  { code:'GT', dial:'+502', flag:'🇬🇹', label:'Guatemala' },
];

function dOnly(s) { return (s || '').replace(/\D/g, ''); }
function toE164(d, l) {
  const dd = dOnly(d), ll = dOnly(l);
  if (!dd || !ll) return '';
  return ll.startsWith(dd) ? `+${ll}` : `+${dd}${ll}`;
}
function fmtPhone(digits, isMx) {
  const d = digits.slice(0, 10);
  if (isMx) {
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)} ${d.slice(3)}`;
    return `${d.slice(0,3)} ${d.slice(3,6)} ${d.slice(6)}`;
  }
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
}

const EMPTY0 = { name:'', phoneRaw:'', street1:'', street2:'', city:'', state:'', zip:'' };

/* ─── Componente principal ─── */
export function AddressForm({
  region = 'mx', lang = 'es',
  totalWeightKg = 0.7, lengthCm = 20, widthCm = 15, heightCm = 10,
  items = [],
  onChange, onRateSelect,
}) {
  const isMx = region === 'mx';
  const destCountry = region === 'mx' ? 'MX' : region === 'ca' ? 'CA' : 'US';

  const navigate = useNavigate();
  const location = useLocation();

  const [form,  setForm]  = useState({ ...EMPTY0 });
  const [dial,  setDial]  = useState(isMx ? '+52' : '+1');
  const [touched, setTouched] = useState({});

  const [quoting,      setQuoting]      = useState(false);
  const [quoteResult,  setQuoteResult]  = useState(null);
  const [selectedRate, setSelectedRate] = useState(null);

  function sf(field, val) {
    setForm(p => ({ ...p, [field]: val }));
    setQuoteResult(null); setSelectedRate(null);
    if (onRateSelect) onRateSelect(null, null);
  }
  function touch(f) { setTouched(p => ({ ...p, [f]: true })); }
  function ferr(f)  { return touched[f] && !String(form[f] || '').trim(); }
  function pErr()   { return touched.phoneRaw && dOnly(form.phoneRaw).length < 7; }
  function handlePhone(raw) { sf('phoneRaw', fmtPhone(dOnly(raw).slice(0, 10), isMx)); }

  /* notify parent */
  useEffect(() => {
    if (!onChange) return;
    onChange({
      name: form.name, phone: toE164(dial, form.phoneRaw),
      street1: form.street1, street2: form.street2 || '',
      city: form.city, state: form.state, zip: form.zip,
      country: isMx ? 'MX' : destCountry,
    });
  }, [form, dial, isMx, destCountry]); // eslint-disable-line

  function handleRegionChange(newRegion) {
    const newPath = location.pathname.replace(/^\/(mx|us|ca)/, `/${newRegion}`);
    navigate(newPath, { replace: true });
  }

  const isCA    = region === 'ca';
  const stateList = isMx ? MX_STATES : destCountry === 'CA' ? CA_PROVINCES : US_STATES;

  const zipValid = isMx
    ? form.zip.trim().length === 5
    : destCountry === 'CA'
      ? /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i.test(form.zip.trim())
      : /^\d{5}$/.test(form.zip.trim());

  const canQuote = Boolean(
    form.name.trim() && dOnly(form.phoneRaw).length >= 7 &&
    form.street1.trim() && form.city.trim() && form.state.trim() && zipValid
  );

  async function handleQuote(e) {
    e.preventDefault();
    if (!canQuote || quoting) return;
    setTouched({ name:1, phoneRaw:1, street1:1, city:1, state:1, zip:1 });
    setQuoting(true); setQuoteResult(null); setSelectedRate(null);
    if (onRateSelect) onRateSelect(null, null);
    const s1 = form.street1;
    try {
      const res = await fetch(`${API}/api/shipping/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: {
            name: form.name.trim(), phone: toE164(dial, form.phoneRaw),
            street1: s1, street2: form.street2 || '',
            city: form.city, state: form.state, zip: form.zip,
            country: isMx ? 'MX' : destCountry,
          },
          package: { weightKg: totalWeightKg, lengthCm, widthCm, heightCm },
          items: items.map(i => ({ id: i.id, quantity: i.qty || i.quantity || 1 })),
        }),
      });
      setQuoteResult(await res.json());
    } catch {
      setQuoteResult({ valid: false, error: 'Error de conexión. Intenta de nuevo.' });
    } finally {
      setQuoting(false);
    }
  }

  function pickRate(rate) {
    setSelectedRate(rate.id);
    if (onRateSelect) onRateSelect(rate, quoteResult.quotationId);
  }

  function fmtR(r) { return `${isMx ? 'MX$' : '$'}${parseFloat(r.amount).toFixed(2)} ${(r.currency || '').toUpperCase()}`; }
  function fmtD(r) {
    if (!r.delivery_days) return '';
    const d = r.delivery_days;
    return lang === 'es' ? `${d}–${d+1} días hábiles` : `${d}–${d+1} business days`;
  }

  const e164 = toE164(dial, form.phoneRaw);

  const T = {
    section:  lang==='es' ? 'DIRECCIÓN DE ENTREGA'                          : lang==='fr' ? 'ADRESSE DE LIVRAISON'              : 'DELIVERY ADDRESS',
    name:     lang==='es' ? 'Nombre completo'                               : lang==='fr' ? 'Nom complet'                      : 'Full name',
    phone:    lang==='es' ? 'Teléfono'                                      : lang==='fr' ? 'Téléphone'                        : 'Phone',
    cp:       lang==='es' ? 'Código postal'                                 : lang==='fr' ? 'Code postal'                      : 'ZIP / Postal code',
    colonia:  lang==='es' ? 'Colonia'                                       : lang==='fr' ? 'Quartier'                         : 'Neighborhood',
    selCol:   lang==='es' ? '— Selecciona tu colonia —'                     : lang==='fr' ? '— Sélectionnez votre quartier —' : '— Select your neighborhood —',
    colHint:  lang==='es' ? 'Ingresa el CP para ver las colonias'           : lang==='fr' ? 'Entrez le CP pour voir les quartiers' : 'Enter ZIP to see neighborhoods',
    ciudad:   lang==='es' ? 'Ciudad / Municipio'                            : lang==='fr' ? 'Ville'                            : 'City',
    estado:   lang==='es' ? 'Estado'                                        : lang==='fr' ? 'Province / État'                  : 'State / Province',
    st1:      lang==='es' ? 'Calle y número exterior'                       : lang==='fr' ? 'Adresse (rue et numéro)'          : 'Street address',
    st2:      lang==='es' ? 'Número interior / Dpto. (opcional)'            : lang==='fr' ? 'App. / Bureau (optionnel)'        : 'Apt / Suite (optional)',
    btn:      lang==='es' ? 'Verificar dirección y calcular envío'          : lang==='fr' ? 'Vérifier et calculer la livraison' : 'Verify address & calculate shipping',
    btnBusy:  lang==='es' ? 'Verificando…'                                  : lang==='fr' ? 'Vérification…'                   : 'Verifying…',
    req:      lang==='es' ? 'Campo requerido'                               : lang==='fr' ? 'Champ requis'                     : 'Required field',
    short:    lang==='es' ? 'Número muy corto'                              : lang==='fr' ? 'Numéro trop court'                : 'Number too short',
    ok:       lang==='es' ? '✅ Dirección verificada'                       : lang==='fr' ? '✅ Adresse vérifiée'               : '✅ Address verified',
    bad:      lang==='es' ? '❌ Problema con la dirección'                  : lang==='fr' ? '❌ Problème avec l\'adresse'       : '❌ Address issue',
    pick:         lang==='es' ? 'SELECCIONA UNA OPCIÓN DE ENVÍO'                        : lang==='fr' ? 'SÉLECTIONNEZ UNE OPTION DE LIVRAISON'       : 'SELECT A SHIPPING OPTION',
    fill:         lang==='es' ? 'Completa todos los campos para calcular envío'        : lang==='fr' ? 'Remplissez tous les champs pour calculer la livraison' : 'Fill all fields to calculate shipping',
    countryLabel: lang==='es' ? 'País de entrega'                                      : lang==='fr' ? 'Pays de livraison'                           : 'Delivery country',
    countryNote:  lang==='es' ? 'Cambia de país para actualizar región y moneda'       : lang==='fr' ? 'Changez de pays pour mettre à jour la région et la devise' : 'Change country to update region and currency',
  };

  const REGION_OPTIONS = [
    { region: 'mx', country: 'MX', flag: '🇲🇽', label: lang==='es' ? 'México (MX$)'          : lang==='fr' ? 'Mexique (MX$)'           : 'Mexico (MX$)'         },
    { region: 'us', country: 'US', flag: '🇺🇸', label: lang==='es' ? 'Estados Unidos (USD)'   : lang==='fr' ? 'États-Unis (USD)'         : 'United States (USD)'  },
    { region: 'ca', country: 'CA', flag: '🇨🇦', label: lang==='es' ? 'Canadá (USD)'           : lang==='fr' ? 'Canada (USD)'             : 'Canada (USD)'         },
  ];

  return (
    <Wrap>
      <SecTitle>{T.section}</SecTitle>

      {/* Selector de país / región — mismo estilo que el header */}
      <div style={{ marginBottom: 20 }}>
        <Label>{T.countryLabel} *</Label>
        <CountryNote>ℹ️ {T.countryNote}</CountryNote>
        <RegionToggle>
          {REGION_OPTIONS.map(o => (
            <RegionBtn
              key={o.region}
              $active={region === o.region}
              onClick={() => handleRegionChange(o.region)}
              type="button"
            >
              <FlagImg code={o.region} size={18} />
              <BtnLabel>{o.label}</BtnLabel>
            </RegionBtn>
          ))}
        </RegionToggle>
      </div>

      <Grid>

        {/* nombre */}
        <Full>
          <Label>{T.name} *</Label>
          <Input value={form.name} $e={ferr('name')}
            onChange={e => sf('name', e.target.value)} onBlur={() => touch('name')}
            placeholder={isMx ? 'Juan García López' : 'John Smith'} />
          {ferr('name') && <Err>{T.req}</Err>}
        </Full>

        {/* teléfono */}
        <Full>
          <Label>{T.phone} *</Label>
          <PhoneRow>
            <DialSel value={dial} onChange={e => setDial(e.target.value)}>
              {DIALS.map(c => (
                <option key={c.code + c.dial} value={c.dial}>{c.flag} {c.dial} {c.label}</option>
              ))}
            </DialSel>
            <PhoneInp type="tel" value={form.phoneRaw} $e={pErr()}
              onChange={e => handlePhone(e.target.value)} onBlur={() => touch('phoneRaw')}
              placeholder={isMx ? '444 123 4567' : '(555) 000-0000'} />
          </PhoneRow>
          {pErr() && <Err>{T.short}</Err>}
          {e164 && dOnly(form.phoneRaw).length >= 7 && <Hint>✓ {e164}</Hint>}
        </Full>

        {/* CP / ZIP / Postal code */}
        <div>
          <Label>{isMx ? T.cp : isCA ? (lang==='es' ? 'Código postal' : 'Postal code') : T.cp} *</Label>
          <Input value={form.zip} $e={touched.zip && !zipValid && !!form.zip || (touched.zip && !form.zip)}
            onChange={e => {
              if (isMx) {
                sf('zip', e.target.value.replace(/\D/g, '').slice(0, 5));
              } else if (isCA) {
                sf('zip', e.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '').slice(0, 7));
              } else {
                sf('zip', e.target.value.replace(/\D/g, '').slice(0, 5));
              }
            }}
            onBlur={() => touch('zip')}
            placeholder={isMx ? '44100' : isCA ? 'K1A 0A9' : '90001'}
            maxLength={isCA ? 7 : 5} />
          {touched.zip && !form.zip && <Err>{T.req}</Err>}
        </div>

        {/* estado / provincia */}
        <div>
          <Label>
            {isMx ? T.estado : isCA ? (lang==='es' ? 'Provincia' : 'Province') : T.estado} *
          </Label>
          <Sel
            value={form.state}
            $ph={!form.state}
            $e={touched.state && !form.state}
            onChange={e => sf('state', e.target.value)}
            onBlur={() => touch('state')}
          >
            <option value="">
              {isMx ? '— Estado —' : isCA ? (lang==='es' ? '— Provincia —' : '— Province —') : '— State —'}
            </option>
            {stateList.map(([abbr, name]) => (
              <option key={abbr} value={abbr}>{abbr} — {name}</option>
            ))}
          </Sel>
          {touched.state && !form.state && <Err>{T.req}</Err>}
        </div>

        {/* ciudad / municipio */}
        <Full>
          <Label>{T.ciudad} *</Label>
          <Input value={form.city}
            onChange={e => sf('city', e.target.value)}
            onBlur={() => touch('city')} $e={touched.city && !form.city}
            placeholder={isMx ? 'Guadalajara' : isCA ? 'Ottawa' : 'Los Angeles'} />
          {touched.city && !form.city && <Err>{T.req}</Err>}
        </Full>

        {/* calle */}
        <Full>
          <Label>{T.st1} *</Label>
          <Input value={form.street1} $e={ferr('street1')}
            onChange={e => sf('street1', e.target.value)} onBlur={() => touch('street1')}
            placeholder={isMx ? 'Av. Independencia 456' : '123 Main St'} />
          {ferr('street1') && <Err>{T.req}</Err>}
        </Full>

        {/* interior */}
        <Full>
          <Label>{T.st2}</Label>
          <Input value={form.street2}
            onChange={e => sf('street2', e.target.value)}
            placeholder={isMx ? 'Depto. 5B, Piso 2' : 'Apt 5B'} />
        </Full>

        {/* botón */}
        <Full>
          <Divider />
          <CalcBtn onClick={handleQuote} disabled={!canQuote || quoting}>
            {quoting
              ? <><Spin style={{ border:'2px solid rgba(255,255,255,.3)', borderTopColor:'#fff' }} /> {T.btnBusy}</>
              : `🚚  ${T.btn}`}
          </CalcBtn>
          {!canQuote && <Hint style={{ textAlign:'center', marginTop:8 }}>{T.fill}</Hint>}
        </Full>

      </Grid>

      {/* resultado */}
      {quoteResult && (
        <ResultBox $ok={quoteResult.valid ? true : quoteResult.rates?.length > 0 ? null : false}>
          <Badge $ok={quoteResult.valid ? true : quoteResult.rates?.length > 0 ? null : false}>
            {quoteResult.valid
              ? T.ok
              : quoteResult.rates?.length > 0
                ? (lang === 'es' ? '⚠️ Dirección con advertencias — tarifas disponibles' : '⚠️ Address warning — rates available')
                : T.bad}
          </Badge>

          {/* advertencias de validación (aunque haya rates) */}
          {!quoteResult.valid && quoteResult.messages?.length > 0 && (
            <p style={{ fontSize:'.78rem', color:'#7C5B00', margin:'0 0 10px', lineHeight:1.5, background:'rgba(255,220,80,.15)', padding:'8px 10px', borderRadius:7 }}>
              ⚠️ {quoteResult.messages.join(' · ')}
              <br/>
              <span style={{ opacity:.7, fontSize:'.72rem' }}>
                {lang === 'es'
                  ? 'Verifica ciudad y estado — puedes editarlos arriba si hay error.'
                  : 'Check city & state above — you can edit them if incorrect.'}
              </span>
            </p>
          )}

          {quoteResult.valid && quoteResult.normalized && (
            <NormAddr>
              {[quoteResult.normalized.street1, quoteResult.normalized.street2,
                quoteResult.normalized.city, quoteResult.normalized.state,
                quoteResult.normalized.zip, quoteResult.normalized.country,
              ].filter(Boolean).join(', ')}
            </NormAddr>
          )}

          {!quoteResult.valid && !quoteResult.rates?.length && (
            <p style={{ fontSize:'.82rem', color:'#C0392B', margin:'4px 0 0', lineHeight:1.5 }}>
              {quoteResult.error}
            </p>
          )}

          {quoteResult.rates?.length > 0 && (
            <>
              <p style={{ fontSize:'.68rem', fontWeight:700, letterSpacing:'.1em', color:'rgba(26,18,8,.5)', margin:'0 0 10px' }}>
                {T.pick}
              </p>
              <RateList>
                {quoteResult.rates.map(rate => (
                  <RateOpt key={rate.id} $sel={selectedRate === rate.id}>
                    <RRadio type="radio" name="ship-rate" value={rate.id}
                      checked={selectedRate === rate.id} onChange={() => pickRate(rate)} />
                    <RInfo>
                      <RName>{rate.carrier} · {rate.service}</RName>
                      {rate.delivery_days && <RDays>📅 {fmtD(rate)}</RDays>}
                    </RInfo>
                    <RPrice>{fmtR(rate)}</RPrice>
                  </RateOpt>
                ))}
              </RateList>
            </>
          )}

          {quoteResult.valid && quoteResult.rates?.length === 0 && (
            <p style={{ fontSize:'.82rem', color:'#C0392B', margin:'8px 0 0' }}>
              {lang === 'es'
                ? 'No hay tarifas disponibles. Verifica que la dirección sea correcta.'
                : 'No shipping rates available. Please verify the address.'}
            </p>
          )}
        </ResultBox>
      )}
    </Wrap>
  );
}

/* ─── Validación para CartPage ─── */
export function validateAddress(addr, lang = 'es') {
  if (!addr) return lang === 'es' ? 'Completa la dirección de entrega' : 'Fill in the delivery address';
  const checks = [
    ['name',    lang === 'es' ? 'Nombre'        : 'Name'],
    ['phone',   lang === 'es' ? 'Teléfono'      : 'Phone'],
    ['street1', lang === 'es' ? 'Calle'         : 'Street'],
    ['city',    lang === 'es' ? 'Ciudad'        : 'City'],
    ['state',   lang === 'es' ? 'Estado'        : 'State'],
    ['zip',     lang === 'es' ? 'Código postal' : 'ZIP'],
  ];
  for (const [f, l] of checks) {
    if (!String(addr[f] || '').trim())
      return lang === 'es' ? `Completa el campo: ${l}` : `Please fill in: ${l}`;
  }
  if (!/^\+\d{8,15}$/.test((addr.phone || '').replace(/\s/g, '')))
    return lang === 'es' ? 'Teléfono inválido' : 'Invalid phone number';
  return null;
}
